import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AiAssistantVoiceService } from '../../services/ai-assistant-voice.service';

/** One staged image attachment shown as a chip until the message is sent. */
interface ComposerAttachment {
  name: string;
  /** base64 data URL, e.g. `data:image/png;base64,…` — sent to the assistant and previewed. */
  dataUrl: string;
  width: number;
  height: number;
}

/** What the composer emits on send: the typed text plus any attached images (base64 data URLs). */
export interface AiComposerSubmit {
  text: string;
  images: string[];
}

/**
 * Message input: auto-growing textarea, Enter-to-send (Shift+Enter for newline), a send
 * button, a feature-detected mic button, and image attachments (pick or paste) for the
 * assistant to read. Voice is transcribed locally (Web Speech API). Images are read to base64
 * and sent on the chat request (docs/ai-assistant-api.md §2).
 */
@Component({
  selector: 'app-ai-assistant-composer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-assistant-composer.component.html',
  styleUrls: ['./ai-assistant-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantComposerComponent implements OnDestroy {
  @ViewChild('input') private input?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  @Input() disabled = false;

  /**
   * Image attach (button + paste) is hidden until the backend can read images (vision).
   * The assistant currently replies "I cannot process images", so we keep the affordance off.
   * Flip to `true` once `POST /ai-assistant/chat` consumes the `images[]` payload.
   */
  readonly attachEnabled = false;

  /** Staged image attachments (chips) sent with the next message. */
  attachments: ComposerAttachment[] = [];

  /** Caps to keep payloads sane — images go inline as base64 on the chat request. */
  private static readonly MAX_IMAGES = 4;
  private static readonly MAX_BYTES = 8 * 1024 * 1024;

  /** When set, replaces the draft and focuses the box (used by welcome suggestions). */
  @Input() set seedText(value: string | null) {
    if (value != null) {
      this.text = value;
      this.draftChange.emit(this.text);
      this.focusSoon();
      this.autoGrowSoon();
    }
  }

  /** Max textarea height before it scrolls (keeps the composer compact). */
  private static readonly MAX_HEIGHT_PX = 110;

  @Output() send = new EventEmitter<AiComposerSubmit>();
  /** The leading "+" affordance starts a fresh conversation. */
  @Output() newChat = new EventEmitter<void>();
  /** Emits the current draft on every change so the welcome screen can declutter while typing. */
  @Output() draftChange = new EventEmitter<string>();

  text = '';
  listening = false;

  private voiceSub?: Subscription;
  private textBeforeVoice = '';

  constructor(
    private readonly voice: AiAssistantVoiceService,
    private readonly toastr: ToastrService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get voiceSupported(): boolean {
    return this.voice.isSupported;
  }

  get canSend(): boolean {
    return !this.disabled && (this.text.trim().length > 0 || this.attachments.length > 0);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  submit(): void {
    if (!this.canSend) {
      return;
    }
    this.stopVoice();
    this.send.emit({ text: this.text.trim(), images: this.attachments.map(a => a.dataUrl) });
    this.text = '';
    this.attachments = [];
    this.draftChange.emit('');
    this.resetHeight();
    this.focusSoon();
  }

  // ---- Image attachments ------------------------------------------------------

  /** Open the OS file picker (image/* only). */
  openFilePicker(): void {
    this.fileInput?.nativeElement?.click();
  }

  /** Files chosen from the picker. Resets the input so the same file can be re-picked. */
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addImages(Array.from(input.files || []));
    input.value = '';
  }

  /** Paste an image straight from the clipboard (screenshots), like the chat you copied from. */
  onPaste(event: ClipboardEvent): void {
    if (!this.attachEnabled) {
      return;
    }
    const items = Array.from(event.clipboardData?.items || []);
    const files = items
      .filter(i => i.kind === 'file' && i.type.startsWith('image/'))
      .map(i => i.getAsFile())
      .filter((f): f is File => !!f);
    if (files.length) {
      event.preventDefault();
      this.addImages(files);
    }
  }

  removeAttachment(index: number): void {
    this.attachments = this.attachments.filter((_, i) => i !== index);
  }

  /** Validate + read each image to a base64 data URL, capturing its pixel dimensions. */
  private addImages(files: File[]): void {
    for (const file of files) {
      if (this.attachments.length >= AiAssistantComposerComponent.MAX_IMAGES) {
        this.toastr.info(`You can attach up to ${AiAssistantComposerComponent.MAX_IMAGES} images.`);
        break;
      }
      if (!file.type.startsWith('image/')) {
        this.toastr.error('Only image files can be attached.');
        continue;
      }
      if (file.size > AiAssistantComposerComponent.MAX_BYTES) {
        this.toastr.error(`"${file.name}" is too large (max ${AiAssistantComposerComponent.MAX_BYTES / (1024 * 1024)} MB).`);
        continue;
      }
      this.readImage(file)
        .then(att => {
          this.attachments = [...this.attachments, att];
          this.cdr.markForCheck();
        })
        .catch(() => this.toastr.error(`Couldn't read "${file.name}".`));
    }
  }

  private readImage(file: File): Promise<ComposerAttachment> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const img = new Image();
        img.onload = () => resolve({ name: file.name, dataUrl, width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve({ name: file.name, dataUrl, width: 0, height: 0 });
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
    });
  }

  /** Handle a keystroke: grow the box and broadcast the draft. */
  onInput(): void {
    this.autoGrow();
    this.draftChange.emit(this.text);
  }

  /** Grow the textarea to fit its content, up to a max, so it starts as one compact line. */
  autoGrow(): void {
    const el = this.input?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, AiAssistantComposerComponent.MAX_HEIGHT_PX) + 'px';
  }

  private resetHeight(): void {
    const el = this.input?.nativeElement;
    if (el) {
      el.style.height = '';
    }
  }

  private autoGrowSoon(): void {
    setTimeout(() => this.autoGrow());
  }

  toggleVoice(): void {
    if (this.listening) {
      this.stopVoice();
      return;
    }
    this.startVoice();
  }

  private startVoice(): void {
    this.textBeforeVoice = this.text ? this.text.trimEnd() + ' ' : '';
    this.listening = true;
    this.voiceSub = this.voice.listen().subscribe({
      next: result => {
        this.text = (this.textBeforeVoice + result.text).trimStart();
        this.draftChange.emit(this.text);
        this.cdr.markForCheck();
        this.autoGrowSoon();
      },
      error: err => {
        this.listening = false;
        this.toastr.error(this.voiceErrorCopy(err));
        this.cdr.markForCheck();
      },
      complete: () => {
        this.listening = false;
        this.focusSoon();
        this.cdr.markForCheck();
      },
    });
  }

  private stopVoice(): void {
    this.voice.stop();
    this.voiceSub?.unsubscribe();
    this.voiceSub = undefined;
    this.listening = false;
  }

  private voiceErrorCopy(err: unknown): string {
    const code = (err as any)?.message || '';
    if (code === 'not-allowed' || code === 'service-not-allowed') {
      return 'Microphone access was blocked. Allow mic permission to use voice input.';
    }
    if (code === 'no-speech') {
      return 'No speech detected. Try again.';
    }
    return 'Voice input is unavailable right now.';
  }

  private focusSoon(): void {
    setTimeout(() => this.input?.nativeElement?.focus());
  }

  ngOnDestroy(): void {
    this.stopVoice();
  }
}
